import type { ApiKeysGuideMessages } from '../guideTypes';

export const guideEn: ApiKeysGuideMessages = {
  documentTitle: 'API keys & Shortcuts — My Budget',
  backHome: 'Home',
  backSettings: 'Settings',
  navAria: 'Guide',

  heroLabel: 'Automation',
  heroTitle: 'API keys & iOS Shortcuts',
  heroLead:
    'Use scoped API keys to log purchases from Shortcuts — without signing into the full web app from your automation.',

  tocLabel: 'On this page',
  tocOverview: 'What API keys are',
  tocCreate: 'Create a key',
  tocShortcuts: 'iOS Shortcuts setup',
  tocFaq: 'FAQ',

  overviewTitle: 'What API keys are',
  overviewBody:
    'An API key is a secret token you create in Settings. Automations send it in the X-API-KEY header so Balance can accept purchases and (optionally) read active budget summaries on your behalf.',
  overviewNotLogin:
    'API keys are for Shortcuts and scripts — not a replacement for Cognito sign-in in the web app.',
  permTitle: 'Permissions',
  permReadCode: 'budget:read',
  permReadBody: 'Allows reading active budget summaries (for example GET /api/v1/budgets/summaries/active).',
  permWriteCode: 'transactions:write',
  permWriteBody:
    'Required to log purchases with POST /api/v1/spending. Grant this for any Shortcuts purchase automation.',
  securityTitle: 'Security tips',
  securityTip1: 'Never share your key in chat, email, or screenshots.',
  securityTip2: 'Copy it when created — the full value is shown only once.',
  securityTip3: 'If a key may have leaked, delete it in Settings and create a new one.',
  securityTip4: 'Prefer one key per device or automation so you can revoke narrowly.',

  createTitle: 'Create a key in Settings',
  createLead: 'You need to be signed in to the web app. Keys are managed under Settings → API Keys.',
  createStep1Title: 'Open Settings',
  createStep1Body: 'Sign in, then open Settings from the bottom navigation (or desktop nav).',
  createStep2Title: 'Name the key',
  createStep2Body: 'Use a clear name such as “iPhone Shortcuts” so you remember what it unlocks.',
  createStep3Title: 'Choose permissions',
  createStep3Body:
    'For purchase logging, enable transactions:write. Add budget:read only if your Shortcut also reads budget summaries.',
  createStep4Title: 'Copy immediately',
  createStep4Body: 'Save the key somewhere secure before dismissing the one-time reveal banner.',
  createFigureCaption:
    'Real Settings → API Keys UI: create a named key with transactions:write, then copy the one-time secret from the amber banner.',
  openSettingsCta: 'Open Settings → API Keys',

  shortcutsTitle: 'Configure iOS Shortcuts',
  shortcutsLead:
    'Card purchases can be logged with a Shortcuts personal automation that calls the spending API. Set it up once; then each matching payment can post in the background.',
  shortcutsEndpointLabel: 'Endpoint',
  shortcutsFieldsTitle: 'JSON body fields',
  fieldCommerce: 'commerceName',
  fieldCommerceHint: 'Store or merchant name (required).',
  fieldAmount: 'amount',
  fieldAmountHint: 'Number or string such as 12.50, 12,50, or €12,50. Optional currency is parsed when present.',
  fieldDate: 'occurredDate',
  fieldDateHint: 'Purchase date in YYYY-MM-DD (required).',
  responseAssigned:
    '201 + status ASSIGNED — a covering root budget existed for that date; a normal transaction was created.',
  responseUnassigned:
    '202 + status UNASSIGNED — no covering budget; the purchase is held for you to assign later in the web app.',

  shortcutStep1Title: 'Create a Wallet / Transaction automation',
  shortcutStep1Body:
    'Open Shortcuts → Automation → +. Choose Transaction (iOS 17–18) or Wallet (newer iOS). Select Payment, pick the cards to track, enable Run Immediately, and turn off Ask Before Running.',
  shortcutStep1Caption:
    'Transaction automation “When I tap”: choose Payment and the Wallet cards to track (real Shortcuts UI).',
  shortcutStep2Title: 'Create a new shortcut action list',
  shortcutStep2Body:
    'Tap Create New Shortcut. Search for Get Contents of URL (or Get Contents of Web Page → configure as URL request). Do not leave fields asking each time if you want silent logging.',
  shortcutStep3Title: 'Set POST URL and headers',
  shortcutStep3Body:
    'Method: POST. URL: your API base + /v1/spending (production example below). Headers: Content-Type = application/json and X-API-KEY = your secret key (not Authorization / Bearer).',
  shortcutStep3Caption:
    'Get Contents of URL with Method POST and Headers — add X-API-KEY with your My Budget secret (illustration from a public Shortcuts screenshot).',
  shortcutStep4Title: 'Send the JSON body',
  shortcutStep4Body:
    'Request body: JSON. Map commerceName, amount, and occurredDate from Shortcut Input (Merchant, Amount, Date) — not Ask Each Time — so Wallet payments fill automatically.',
  shortcutStep4Caption:
    'Use Shortcut Input properties Merchant / Amount / Date for the JSON fields — avoid Ask Each Time.',
  shortcutStep5Title: 'Save and test',
  shortcutStep5Body:
    'Save the automation. Make a small Apple Pay purchase or run a manual test Shortcut with sample JSON. Confirm the spend appears in My Budget, or as unassigned if no budget covers that date.',
  shortcutStep6Title: 'Wire payment fields carefully',
  shortcutStep6Body:
    'If Shortcuts prompts for merchant or amount after paying, fields are not mapped to Shortcut Input. Edit each field → Select Variable → Shortcut Input → Merchant / Amount / Date.',
  exampleJsonLabel: 'Example request body',

  faqTitle: 'FAQ',
  faq1Q: 'Where do I create an API key?',
  faq1A:
    'Sign in to the web app → Settings → API Keys. Create a named key, copy the secret once, then paste it into your Shortcut header.',
  faq2Q: 'Which permissions do I need?',
  faq2A:
    'transactions:write for POST /api/v1/spending. budget:read only if you also call active budget summaries. Grant the minimum you need.',
  faq3Q: 'Why do I get HTTP 401?',
  faq3A:
    'Usually the X-API-KEY header is missing, mistyped, or the key was deleted. Header name must be exactly X-API-KEY. Creating a new key invalidates nothing else — but the old secret stops working once revoked.',
  faq4Q: 'Why do I get HTTP 403?',
  faq4A:
    'The key authenticated but lacks the grant for that endpoint (for example calling spending without transactions:write). Edit permissions in Settings or create a new key with the right scopes.',
  faq5Q: 'Do I need HTTPS?',
  faq5A:
    'Yes for production. Use https://budget.florez.xyz/api/v1/spending (or your deployed API over HTTPS). iOS Shortcuts generally require HTTPS for remote URLs outside local development.',
  faq6Q: 'What if there is no covering budget?',
  faq6A:
    'The API returns 202 with status UNASSIGNED and stores the purchase for later. Open the web app to assign or discard unassigned spending — it does not affect budget totals until assigned.',
  faq7Q: 'Can an API key sign me into the web app?',
  faq7A:
    'No. Keys only unlock annotated automation endpoints. Day-to-day use of My Budget still uses Cognito sign-in.',
  faq8Q: 'How should amount and date look?',
  faq8A:
    'amount accepts flexible strings (12.50, 12,50, €12,50). occurredDate must be ISO date YYYY-MM-DD (for example 2026-07-06).',

  footerNote: 'My Budget automation docs — scoped keys for Shortcuts, not full account login.',
};
